/**
 * CompanySettings.jsx
 * 
 * Company/Business settings management for account owners.
 * Uses existing /company/profile endpoint.
 */

import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import api from './axiosConfig';
import {
    Box,
    Container,
    Card,
    CardContent,
    Typography,
    Button,
    Paper,
    CircularProgress,
    Alert,
    Grid,
    TextField,
    MenuItem,
    FormControl,
    InputLabel,
    Select,
    FormControlLabel,
    Switch,
    Snackbar
} from '@mui/material';
import {
    Business as BusinessIcon,
    Home as HomeIcon,
    Save as SaveIcon,
    DateRange as HolidayIcon,
    Receipt as VATIcon,
    LocationOn as LocationIcon,
    AccessTime as ClockIcon
} from '@mui/icons-material';

const CompanySettings = () => {
    const navigate = useNavigate();
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState(null);
    const [success, setSuccess] = useState(false);
    
    const [formData, setFormData] = useState({
        business_name: '',
        business_address_line1: '',
        business_address_line2: '',
        business_address_line3: '',
        postcode: '',
        phone_number: '',
        email: '',
        // Legal structure
        legal_structure: '',
        // Holiday settings
        holiday_year_start_month: 1,
        holiday_year_start_day: 1,
        total_holiday_days: 28,
        // VAT settings
        is_vat_registered: false,
        vat_scheme: '',
        vat_number: '',
        vat_registration_date: '',
        // Clock-in settings
        clock_in_enabled: true,
        require_location_clock: false,
        latitude: '',
        longitude: '',
        clock_radius_meters: 150
    });

    const vatSchemes = [
        { value: 'STANDARD_RATE', label: 'Standard Rate (20%)' },
        { value: 'FLAT_RATE', label: 'Flat Rate Scheme' },
        { value: 'FIRST_YEAR_FLAT_RATE', label: 'First Year Flat Rate' }
    ];

    const months = [
        { value: 1, label: 'January' },
        { value: 2, label: 'February' },
        { value: 3, label: 'March' },
        { value: 4, label: 'April' },
        { value: 5, label: 'May' },
        { value: 6, label: 'June' },
        { value: 7, label: 'July' },
        { value: 8, label: 'August' },
        { value: 9, label: 'September' },
        { value: 10, label: 'October' },
        { value: 11, label: 'November' },
        { value: 12, label: 'December' }
    ];

    useEffect(() => {
        fetchCompanyInfo();
    }, []);

    const fetchCompanyInfo = async () => {
        try {
            setLoading(true);
            const res = await api.get('/company/profile');
            const data = res.data;
            
            setFormData({
                business_name: data.business_name || '',
                business_address_line1: data.business_address_line1 || '',
                business_address_line2: data.business_address_line2 || '',
                business_address_line3: data.business_address_line3 || '',
                postcode: data.postcode || '',
                phone_number: data.phone_number || '',
                email: data.email || '',
                legal_structure: data.legal_structure || '',
                holiday_year_start_month: data.holiday_year_start_month || 1,
                holiday_year_start_day: data.holiday_year_start_day || 1,
                total_holiday_days: data.total_holiday_days || 28,
                is_vat_registered: data.is_vat_registered || false,
                vat_scheme: data.vat_scheme || '',
                vat_number: data.vat_number || '',
                vat_registration_date: data.vat_registration_date || '',
                clock_in_enabled: data.clock_in_enabled !== false,
                require_location_clock: data.require_location_clock || false,
                latitude: data.latitude || '',
                longitude: data.longitude || '',
                clock_radius_meters: data.clock_radius_meters || 150
            });
        } catch (err) {
            console.error('Failed to fetch company info', err);
            setError('Failed to load company settings');
        } finally {
            setLoading(false);
        }
    };

    const handleChange = (e) => {
        const { name, value, type, checked } = e.target;
        
        // Handle number inputs
        if (['holiday_year_start_month', 'holiday_year_start_day', 'total_holiday_days', 'clock_radius_meters'].includes(name)) {
            const numValue = parseInt(value) || 0;
            if (name === 'holiday_year_start_day' && (numValue < 1 || numValue > 31)) {
                return;
            }
            setFormData(prev => ({ ...prev, [name]: numValue }));
            return;
        }

        // Handle latitude/longitude
        if (['latitude', 'longitude'].includes(name)) {
            setFormData(prev => ({ ...prev, [name]: value }));
            return;
        }

        setFormData(prev => ({
            ...prev,
            [name]: type === 'checkbox' || type === 'switch' ? checked : value,
            // Clear VAT fields if un-registering
            ...(name === 'is_vat_registered' && !checked ? {
                vat_scheme: '',
                vat_number: '',
                vat_registration_date: ''
            } : {})
        }));
    };

    const handleSwitchChange = (name) => (e) => {
        setFormData(prev => ({ ...prev, [name]: e.target.checked }));
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setSaving(true);
        setError(null);

        try {
            await api.put('/company/profile', formData);
            setSuccess(true);
        } catch (err) {
            console.error('Failed to save company settings', err);
            setError(err.response?.data?.error || 'Failed to save settings');
        } finally {
            setSaving(false);
        }
    };

    const handleGetLocation = () => {
        if (navigator.geolocation) {
            navigator.geolocation.getCurrentPosition(
                (position) => {
                    setFormData(prev => ({
                        ...prev,
                        latitude: position.coords.latitude.toFixed(8),
                        longitude: position.coords.longitude.toFixed(8)
                    }));
                },
                (error) => {
                    setError('Unable to get location. Please enter coordinates manually.');
                }
            );
        } else {
            setError('Geolocation is not supported by this browser.');
        }
    };

    if (loading) {
        return (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '60vh' }}>
                <CircularProgress />
            </Box>
        );
    }

    return (
        <Box>
            {/* Header */}
            <Paper
                elevation={0}
                sx={{
                    background: 'linear-gradient(135deg, #3b82f6, #1d4ed8)',
                    color: 'white',
                    p: 4,
                    mb: 4,
                    borderRadius: 0
                }}
            >
                <Container maxWidth="lg">
                    <Button
                        startIcon={<HomeIcon />}
                        onClick={() => navigate('/dashboard')}
                        sx={{ color: 'white', mb: 2, '&:hover': { bgcolor: 'rgba(255,255,255,0.1)' } }}
                    >
                        Back to Dashboard
                    </Button>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                        <Box sx={{ 
                            width: 56, 
                            height: 56, 
                            borderRadius: 2, 
                            bgcolor: 'rgba(255,255,255,0.2)',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center'
                        }}>
                            <BusinessIcon sx={{ fontSize: 28 }} />
                        </Box>
                        <Box>
                            <Typography variant="h4" sx={{ fontWeight: 600, mb: 0.5 }}>
                                Company Settings
                            </Typography>
                            <Typography variant="body1" sx={{ opacity: 0.9 }}>
                                Manage your business details, holiday settings, and VAT configuration
                            </Typography>
                        </Box>
                    </Box>
                </Container>
            </Paper>

            <Container maxWidth="lg">
                {error && (
                    <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>{error}</Alert>
                )}

                <form onSubmit={handleSubmit}>
                    <Grid container spacing={3}>
                        {/* Business Details */}
                        <Grid item xs={12} md={6}>
                            <Card elevation={0} sx={{ border: '1px solid #e5e7eb', height: '100%' }}>
                                <CardContent sx={{ p: 3 }}>
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3 }}>
                                        <Box sx={{ 
                                            width: 40, 
                                            height: 40, 
                                            borderRadius: 2, 
                                            bgcolor: '#dbeafe',
                                            color: '#3b82f6',
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'center'
                                        }}>
                                            <BusinessIcon />
                                        </Box>
                                        <Typography variant="h6" sx={{ fontWeight: 600 }}>
                                            Business Details
                                        </Typography>
                                    </Box>

                                    <Grid container spacing={2}>
                                        <Grid item xs={12}>
                                            <TextField
                                                fullWidth
                                                name="business_name"
                                                label="Business Name"
                                                value={formData.business_name}
                                                onChange={handleChange}
                                                required
                                            />
                                        </Grid>
                                        <Grid item xs={12}>
                                            <TextField
                                                fullWidth
                                                name="business_address_line1"
                                                label="Address Line 1"
                                                value={formData.business_address_line1}
                                                onChange={handleChange}
                                            />
                                        </Grid>
                                        <Grid item xs={12} sm={6}>
                                            <TextField
                                                fullWidth
                                                name="business_address_line2"
                                                label="Address Line 2"
                                                value={formData.business_address_line2}
                                                onChange={handleChange}
                                            />
                                        </Grid>
                                        <Grid item xs={12} sm={6}>
                                            <TextField
                                                fullWidth
                                                name="business_address_line3"
                                                label="Address Line 3"
                                                value={formData.business_address_line3}
                                                onChange={handleChange}
                                            />
                                        </Grid>
                                        <Grid item xs={12} sm={6}>
                                            <TextField
                                                fullWidth
                                                name="postcode"
                                                label="Postcode"
                                                value={formData.postcode}
                                                onChange={handleChange}
                                            />
                                        </Grid>
                                        <Grid item xs={12} sm={6}>
                                            <TextField
                                                fullWidth
                                                name="phone_number"
                                                label="Phone Number"
                                                value={formData.phone_number}
                                                onChange={handleChange}
                                            />
                                        </Grid>
                                        <Grid item xs={12}>
                                            <TextField
                                                fullWidth
                                                name="email"
                                                label="Business Email"
                                                type="email"
                                                value={formData.email}
                                                onChange={handleChange}
                                            />
                                        </Grid>
                                    </Grid>
                                </CardContent>
                            </Card>
                        </Grid>

                        {/* Holiday Settings */}
                        <Grid item xs={12} md={6}>
                            <Card elevation={0} sx={{ border: '1px solid #e5e7eb', height: '100%' }}>
                                <CardContent sx={{ p: 3 }}>
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3 }}>
                                        <Box sx={{ 
                                            width: 40, 
                                            height: 40, 
                                            borderRadius: 2, 
                                            bgcolor: '#d1fae5',
                                            color: '#059669',
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'center'
                                        }}>
                                            <HolidayIcon />
                                        </Box>
                                        <Typography variant="h6" sx={{ fontWeight: 600 }}>
                                            Holiday Year Settings
                                        </Typography>
                                    </Box>

                                    <Alert severity="info" sx={{ mb: 3 }}>
                                        <Typography variant="body2">
                                            <strong>Important:</strong> Changing the holiday year start date will affect 
                                            how annual leave is calculated and accrued for all employees.
                                        </Typography>
                                    </Alert>

                                    <Grid container spacing={2}>
                                        <Grid item xs={12} sm={6}>
                                            <FormControl fullWidth>
                                                <InputLabel>Holiday Year Start Month</InputLabel>
                                                <Select
                                                    name="holiday_year_start_month"
                                                    value={formData.holiday_year_start_month}
                                                    onChange={handleChange}
                                                    label="Holiday Year Start Month"
                                                >
                                                    {months.map(month => (
                                                        <MenuItem key={month.value} value={month.value}>
                                                            {month.label}
                                                        </MenuItem>
                                                    ))}
                                                </Select>
                                            </FormControl>
                                        </Grid>
                                        <Grid item xs={12} sm={6}>
                                            <TextField
                                                fullWidth
                                                type="number"
                                                name="holiday_year_start_day"
                                                label="Start Day"
                                                value={formData.holiday_year_start_day}
                                                onChange={handleChange}
                                                inputProps={{ min: 1, max: 31 }}
                                            />
                                        </Grid>
                                        <Grid item xs={12}>
                                            <TextField
                                                fullWidth
                                                type="number"
                                                name="total_holiday_days"
                                                label="Default Annual Holiday Entitlement (Days)"
                                                value={formData.total_holiday_days}
                                                onChange={handleChange}
                                                inputProps={{ min: 0 }}
                                                helperText="UK statutory minimum is 28 days (including bank holidays)"
                                            />
                                        </Grid>
                                    </Grid>
                                </CardContent>
                            </Card>
                        </Grid>

                        {/* VAT Settings */}
                        <Grid item xs={12} md={6}>
                            <Card elevation={0} sx={{ border: '1px solid #e5e7eb', height: '100%' }}>
                                <CardContent sx={{ p: 3 }}>
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3 }}>
                                        <Box sx={{ 
                                            width: 40, 
                                            height: 40, 
                                            borderRadius: 2, 
                                            bgcolor: '#fef3c7',
                                            color: '#d97706',
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'center'
                                        }}>
                                            <VATIcon />
                                        </Box>
                                        <Typography variant="h6" sx={{ fontWeight: 600 }}>
                                            VAT Settings
                                        </Typography>
                                    </Box>
                                    
                                    <Grid item xs={12}>
                                        <FormControl fullWidth required>
                                            <InputLabel>Legal Structure</InputLabel>
                                            <Select
                                              name="legal_structure"
                                              value={formData.legal_structure}
                                              onChange={handleChange}
                                              label="Legal Structure"
                                            >
                                              <MenuItem value="SOLE_TRADER">Sole Trader</MenuItem>
                                              <MenuItem value="LIMITED_COMPANY">Limited Company</MenuItem>
                                            </Select>
                                         </FormControl>
                                    </Grid>

                                    <Grid container spacing={2}>
                                        <Grid item xs={12}>
                                            <FormControlLabel
                                                control={
                                                    <Switch
                                                        checked={formData.is_vat_registered}
                                                        onChange={handleSwitchChange('is_vat_registered')}
                                                        color="primary"
                                                    />
                                                }
                                                label="Business is VAT Registered"
                                            />
                                        </Grid>
                                        
                                        {formData.is_vat_registered && (
                                            <>
                                                <Grid item xs={12} sm={6}>
                                                    <TextField
                                                        fullWidth
                                                        name="vat_number"
                                                        label="VAT Number"
                                                        value={formData.vat_number}
                                                        onChange={handleChange}
                                                        required={formData.is_vat_registered}
                                                    />
                                                </Grid>
                                                <Grid item xs={12} sm={6}>
                                                    <FormControl fullWidth required={formData.is_vat_registered}>
                                                        <InputLabel>VAT Scheme</InputLabel>
                                                        <Select
                                                            name="vat_scheme"
                                                            value={formData.vat_scheme}
                                                            onChange={handleChange}
                                                            label="VAT Scheme"
                                                        >
                                                            {vatSchemes.map(scheme => (
                                                                <MenuItem key={scheme.value} value={scheme.value}>
                                                                    {scheme.label}
                                                                </MenuItem>
                                                            ))}
                                                        </Select>
                                                    </FormControl>
                                                </Grid>
                                                <Grid item xs={12} sm={6}>
                                                    <TextField
                                                        fullWidth
                                                        name="vat_registration_date"
                                                        label="VAT Registration Date"
                                                        type="date"
                                                        value={formData.vat_registration_date}
                                                        onChange={handleChange}
                                                        required={formData.is_vat_registered}
                                                        InputLabelProps={{ shrink: true }}
                                                        helperText="The date HMRC registered you for VAT"
                                                    />
                                                </Grid>
                                            </>
                                        )}
                                    </Grid>
                                </CardContent>
                            </Card>
                        </Grid>

                        {/* Clock-in / Location Settings */}
                        <Grid item xs={12} md={6}>
                            <Card elevation={0} sx={{ border: '1px solid #e5e7eb', height: '100%' }}>
                                <CardContent sx={{ p: 3 }}>
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3 }}>
                                        <Box sx={{ 
                                            width: 40, 
                                            height: 40, 
                                            borderRadius: 2, 
                                            bgcolor: '#f3e8ff',
                                            color: '#7c3aed',
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'center'
                                        }}>
                                            <ClockIcon />
                                        </Box>
                                        <Typography variant="h6" sx={{ fontWeight: 600 }}>
                                            Clock-in Settings
                                        </Typography>
                                    </Box>

                                    <Grid container spacing={2}>
                                        <Grid item xs={12}>
                                            <FormControlLabel
                                                control={
                                                    <Switch
                                                        checked={formData.clock_in_enabled}
                                                        onChange={handleSwitchChange('clock_in_enabled')}
                                                        color="primary"
                                                    />
                                                }
                                                label="Enable Clock-in/Clock-out for Staff"
                                            />
                                        </Grid>

                                        {formData.clock_in_enabled && (
                                            <>
                                                <Grid item xs={12}>
                                                    <FormControlLabel
                                                        control={
                                                            <Switch
                                                                checked={formData.require_location_clock}
                                                                onChange={handleSwitchChange('require_location_clock')}
                                                                color="primary"
                                                            />
                                                        }
                                                        label="Require Location Verification for Clock-in"
                                                    />
                                                </Grid>

                                                {formData.require_location_clock && (
                                                    <>
                                                        <Grid item xs={12}>
                                                            <Alert severity="info" sx={{ mb: 2 }}>
                                                                <Typography variant="body2">
                                                                    Set your salon's location to ensure staff can only clock in when they're on-site.
                                                                </Typography>
                                                            </Alert>
                                                        </Grid>
                                                        <Grid item xs={12} sm={5}>
                                                            <TextField
                                                                fullWidth
                                                                name="latitude"
                                                                label="Latitude"
                                                                value={formData.latitude}
                                                                onChange={handleChange}
                                                                placeholder="e.g., 51.5074"
                                                            />
                                                        </Grid>
                                                        <Grid item xs={12} sm={5}>
                                                            <TextField
                                                                fullWidth
                                                                name="longitude"
                                                                label="Longitude"
                                                                value={formData.longitude}
                                                                onChange={handleChange}
                                                                placeholder="e.g., -0.1278"
                                                            />
                                                        </Grid>
                                                        <Grid item xs={12} sm={2}>
                                                            <Button
                                                                variant="outlined"
                                                                onClick={handleGetLocation}
                                                                fullWidth
                                                                sx={{ height: '56px' }}
                                                                title="Get Current Location"
                                                            >
                                                                <LocationIcon />
                                                            </Button>
                                                        </Grid>
                                                        <Grid item xs={12}>
                                                            <TextField
                                                                fullWidth
                                                                type="number"
                                                                name="clock_radius_meters"
                                                                label="Allowed Radius (meters)"
                                                                value={formData.clock_radius_meters}
                                                                onChange={handleChange}
                                                                inputProps={{ min: 10, max: 1000 }}
                                                                helperText="Staff must be within this distance to clock in"
                                                            />
                                                        </Grid>
                                                    </>
                                                )}
                                            </>
                                        )}
                                    </Grid>
                                </CardContent>
                            </Card>
                        </Grid>

                        {/* Save Button */}
                        <Grid item xs={12}>
                            <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 2, mt: 2 }}>
                                <Button
                                    variant="outlined"
                                    onClick={() => navigate('/dashboard')}
                                    sx={{ borderColor: '#e5e7eb', color: 'text.secondary' }}
                                >
                                    Cancel
                                </Button>
                                <Button
                                    type="submit"
                                    variant="contained"
                                    startIcon={saving ? <CircularProgress size={20} color="inherit" /> : <SaveIcon />}
                                    disabled={saving}
                                    sx={{
                                        background: 'linear-gradient(135deg, #3b82f6, #1d4ed8)',
                                        '&:hover': { opacity: 0.9 }
                                    }}
                                >
                                    {saving ? 'Saving...' : 'Save Changes'}
                                </Button>
                            </Box>
                        </Grid>
                    </Grid>
                </form>
            </Container>

            {/* Success Snackbar */}
            <Snackbar
                open={success}
                autoHideDuration={3000}
                onClose={() => setSuccess(false)}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
            >
                <Alert severity="success" onClose={() => setSuccess(false)}>
                    Company settings saved successfully!
                </Alert>
            </Snackbar>
        </Box>
    );
};

export default CompanySettings;